<template>
  <section class="war-room-post-poll" :class="pollClasses">
    <div class="war-room-post-poll__header">
      <MegaphoneIcon class="war-room-post-poll__icon" />
      <BravadoText v-if="poll" variant="subhead-semibold" class="war-room-post-poll__title">
        {{ poll.title }}
      </BravadoText>
    </div>

    <template v-if="isChoicesVisible">
      <RadioButtonGroup
        v-if="votable"
        :config="radioButtonsConfig"
        group="pollChoices"
        class="war-room-post-poll__choices"
        @change="onPollVote"
      />
      <WarRoomPostPollResults v-else :poll="poll" />
    </template>

    <BravadoText v-if="votersCount" variant="body" class="war-room-post-poll__count">
      {{ votersCount }} people voted
    </BravadoText>

    <div v-if="showHint" class="war-room-post-poll__hint">*Voting in this poll no longer yields commission.</div>
  </section>
</template>

<script lang="ts" setup>
import { computed, useContext } from '@nuxtjs/composition-api';
import BravadoText from '@/components/common/BravadoText/BravadoText.vue';
import RadioButtonGroup from '@/components/common/RadioButtonGroup/RadioButtonGroup.vue';
import MegaphoneIcon from '@/components/partials/war-room/WarRoomPostPollV2/images/megaphone-icon.vue.svg';
import WarRoomPostPollResults from '@/components/partials/war-room/WarRoomPostPollV2/WarRoomPostPollV2Results/WarRoomPostPollV2Results.vue';
import { sortByField } from '@/components/utils/sorting_func';
import type { IPost } from '@/types/community';

type WarRoomPostPollV2Props = {
  post: IPost;
};
type WarRoomPostPollV2Emits = {
  (event: 'vote', choiceId: number): void;
};

const props = defineProps<WarRoomPostPollV2Props>();
const emit = defineEmits<WarRoomPostPollV2Emits>();

const { $auth } = useContext();

const poll = computed(() => props.post.poll);
const isChoicesVisible = computed(() => poll.value && poll.value.choices.length < 5);
const isAuthor = computed(() => props.post.user_username === $auth.user?.username);
const votable = computed(() => {
  if (isAuthor.value) return false;

  return !!poll.value?.choices.some(({ current_vote: currentVote }) => currentVote);
});
const votersCount = computed(() => poll.value?.votes_count ?? 0);
const showHint = computed(() => isChoicesVisible.value && votable.value && !!poll.value?.expired_for_commission);
const radioButtonsConfig = computed(() =>
  poll.value?.choices
    .map(({ id, text, position }) => ({
      label: text,
      value: id.toString(),
      position,
      size: 'sm',
    }))
    .sort(sortByField('position')),
);
const pollClasses = computed(() => ({
  'war-room-post-poll--disabled': !$auth.loggedIn,
  'war-room-post-poll--pinned': props.post.pinned,
}));

function onPollVote(choiceId: number) {
  if ($auth.loggedIn) emit('vote', choiceId);
}
</script>

<style scoped lang="scss">
.war-room-post-poll {
  padding: 12px;
  background: var(--theme-color-over-card);
  border-radius: 8px;
  flex-grow: 1;
  pointer-events: none;

  &--pinned {
    background: rgba(0, 0, 0, 0.05);
  }

  &--disabled {
    & > * {
      pointer-events: none !important;
    }
  }

  &__header {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  &__choices {
    pointer-events: all;
  }

  &__count {
    margin-top: 12px;
    color: var(--theme-color-sub-text);
  }

  &__icon {
    margin-right: 6px;
    flex-shrink: 0;
  }

  &__title {
    color: var(--theme-color-text);
  }

  ::v-deep .radio-button-group__item {
    @include subhead;

    margin-bottom: 0;
    margin-top: 12px;
  }

  &__hint {
    @include caption2;

    margin-top: 12px;
    color: var(--gray-50);
  }
}
</style>
